<template>
  <Teleport to="body">
    <div v-if="isOpen" class="modal-backdrop" :style="{ zIndex }" @click.self="handleClose">
      <div class="modal-container shadow-lg" role="dialog" aria-modal="true">

        <header class="modal-header">
          <h5 class="modal-title d-flex align-items-center">
            <i class="bi bi-question-circle-fill me-2 text-warning"></i>
            {{ title }}
          </h5>
          <button type="button" class="btn-close btn-close-white" @click="handleClose" aria-label="Fechar"></button>
        </header>

        <div class="modal-body bg-body px-4 py-4">
          <p class="mb-0 fs-5 text-center text-body-emphasis" v-html="message"></p>
        </div>

        <footer class="modal-footer justify-content-center gap-3 bg-body-tertiary">
          <button type="button" class="btn btn-outline-secondary px-4" @click="emitResult(false)">
            {{ cancelButtonText }}
          </button>
          <button type="button" class="btn btn-primary px-4 shadow-sm" @click="emitResult(true)">
            {{ confirmButtonText }}
          </button>
        </footer>

      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, onUnmounted } from 'vue'
import { reservarZIndex, liberarZIndex, BASE_ZINDEX } from '@/composables/useZIndex'

defineProps({
  title: {
    type: String,
    default: 'Confirmação'
  },
  message: {
    type: String,
    default: 'Você tem certeza?'
  },
  confirmButtonText: {
    type: String,
    default: 'Confirmar'
  },
  cancelButtonText: {
    type: String,
    default: 'Cancelar'
  }
})

const emit = defineEmits(['confirmed', 'close'])

// Estado Reativo do Modal
const isOpen = ref(false)
const resultEmitted = ref(false)

// --- Empilhamento (z-index) via registry central ---
// Reserva o próximo z-index ao abrir e libera ao fechar, garantindo que a
// confirmação fique sempre à frente do modal que a disparou.
const zIndex = ref(BASE_ZINDEX)
const tokenZIndex = ref(null)

// Métodos expostos para o componente Pai (ex: MainDashboard)
const show = () => {
  resultEmitted.value = false
  const { token, zIndex: z } = reservarZIndex()
  tokenZIndex.value = token
  zIndex.value = z
  isOpen.value = true
}

const hide = () => {
  isOpen.value = false
  liberarZIndex(tokenZIndex.value)
  tokenZIndex.value = null
}

// Segurança: libera o z-index caso o componente seja desmontado ainda aberto
onUnmounted(() => {
  liberarZIndex(tokenZIndex.value)
  tokenZIndex.value = null
})

// Fechamento sem decisão (backdrop/X) — não emite 'confirmed'
const handleClose = () => {
  if (!resultEmitted.value) {
    resultEmitted.value = true
    emit('close')
    hide()
  }
}

// Emissão de confirmação (botão Sim/Não)
const emitResult = (result) => {
  if (!resultEmitted.value) {
    resultEmitted.value = true
    emit('confirmed', result)
    hide()
  }
}

// Expõe os métodos para serem chamados via "ref" no MainDashboard
defineExpose({
  show,
  hide
})
</script>

<style scoped>
/* GUARDIÃO: O fundo translúcido padrão do sistema. O z-index é calculado
   dinamicamente pelo registry central (useZIndex) para se sobrepor ao modal
   que disparou a confirmação. */
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: transparent;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Base adaptável e fluida */
.modal-container {
  width: 90%;
  max-width: 450px; /* Tamanho contido e elegante para confirmações */
  background-color: var(--bs-body-bg);
  color: var(--bs-body-color);
  border: 1px solid var(--bs-border-color-translucent);
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

/* O Cabeçalho mantém a âncora visual da marca, tal como os outros modais */
.modal-header {
  padding: 1rem 1.5rem;
  background-color: #34495E;
  color: #FFFFFF;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #1f2d3a;
  flex-shrink: 0;
  border-top-left-radius: 0.5rem;
  border-top-right-radius: 0.5rem;
}

.modal-title {
  font-size: 1.15rem;
  font-weight: 500;
  margin: 0;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex-grow: 1;
}

.modal-footer {
  padding: 1.25rem 1.5rem;
  background-color: var(--bs-body-bg);
  border-top: 1px solid var(--bs-border-color-translucent);
  flex-shrink: 0;
  display: flex;
  border-bottom-left-radius: 0.5rem;
  border-bottom-right-radius: 0.5rem;
  transition: background-color 0.3s ease, border-color 0.3s ease;
}
</style>
